// Location-specific content for SEO and GEO optimization

export interface LocationContent {
    name: string;
    province: string;
    description: string;
    metaDescription: string;
    landmarks: string[];
    universities: string[];
    transport: string[];
    businessHubs: string[];
    nearbySuburbs?: string[];
    latitude?: number;
    longitude?: number;
    faqs: { question: string; answer: string }[];
}

export const locationContent: Record<string, LocationContent> = {
    'pretoria': {
        name: 'Pretoria',
        province: 'Gauteng',
        description: 'Pretoria, the administrative capital of South Africa, offers diverse accommodation options for students and professionals. Known for its jacaranda-lined streets and as an educational hub, Pretoria hosts multiple universities and accessible public transport.',
        metaDescription: 'Find affordable rooms for rent in Pretoria. Browse verified student accommodation and rooms near universities, transport, and business hubs. Rooms from R1,500/month.',
        landmarks: ['Union Buildings', 'Voortrekker Monument', 'Freedom Park', 'Pretoria Botanical Gardens'],
        universities: ['University of Pretoria', 'Tshwane University of Technology', 'Sefako Makgatho Health Sciences University', 'UNISA'],
        transport: ['Gautrain Station (Hatfield, Pretoria)', 'PUTCO Bus Routes', 'Metrorail Pretoria', 'A Re Yeng Bus Rapid Transit'],
        businessHubs: ['Menlyn Maine', 'Brooklyn', 'Centurion', 'Hatfield'],
        nearbySuburbs: ['Hatfield', 'Arcadia', 'Sunnyside', 'Brooklyn', 'Lynnwood', 'Menlyn'],
        latitude: -25.7479,
        longitude: 28.2293,
        faqs: [
            {
                question: 'What is the average rent for rooms in Pretoria?',
                answer: 'Room rentals in Pretoria typically range from R1,500 to R4,500 per month, depending on the area, amenities, and proximity to universities or transport hubs. Student areas like Hatfield and Sunnyside tend to be more affordable.'
            },
            {
                question: 'Which areas in Pretoria are best for students?',
                answer: 'Popular student areas include Hatfield (close to UP), Sunnyside, Arcadia, Brooklyn, and Lynnwood. These suburbs offer good access to universities, affordable accommodation, and convenient transport.'
            },
            {
                question: 'Is public transport easily accessible in Pretoria?',
                answer: 'Yes, Pretoria has good public transport including the Gautrain (Hatfield station), A Re Yeng BRT buses, PUTCO buses, and Metrorail services connecting major suburbs and universities.'
            }
        ]
    },

    'johannesburg': {
        name: 'Johannesburg',
        province: 'Gauteng',
        description: 'Johannesburg offers a vibrant student life with world-class education. Our rooms are strategically located near Wits University and UJ campuses, ensuring you spend less time commuting and more time studying and living. We target key student hubs like Braamfontein, Auckland Park, and Melville.',
        metaDescription: 'Find affordable student accommodation in Johannesburg near UJ and Wits. Rooms in Braamfontein, Auckland Park, Melville from R1,800/month. Verified & secure.',
        landmarks: ['Johannesburg CBD', 'Nelson Mandela Bridge', 'Constitution Hill', 'Campus Square', 'Neighbourgoods Market'],
        universities: ['University of Johannesburg (UJ - APK, APB, Doornfontein)', 'Wits University', 'Rosebank College', 'Boston Media House'],
        transport: ['Rea Vaya BRT (connecting campuses)', 'Gautrain (Park Station, Rosebank)', 'UJ Shuffle Buses', 'Wits Circuit Buses'],
        businessHubs: ['Braamfontein', 'Rosebank', 'Johannesburg CBD', 'Newtown'],
        nearbySuburbs: ['Braamfontein', 'Auckland Park', 'Melville', 'Westdene', 'Brixton', 'Doornfontein', 'Rosebank'],
        latitude: -26.2041,
        longitude: 28.0473,
        faqs: [
            {
                question: 'Do you have rooms near UJ Auckland Park (APK) campus?',
                answer: 'Yes, we specialize in accommodation in Auckland Park, Westdene, and Melville, all within walking distance or a short shuttle ride to UJ APK and APB campuses.'
            },
            {
                question: 'Where is the best place to live for Wits students?',
                answer: 'Braamfontein is the top choice for Wits students due to its proximity to the main campus. We also offer options in Parktown and nearby suburbs with easy access to university transport.'
            },
            {
                question: 'Are the rooms NSFAS accredited?',
                answer: 'Many of our listed properties meet NSFAS standards. Look for the "NSFAS Accredited" tag on our listings or filter by accreditation status.'
            }
        ]
    },

    'cape-town': {
        name: 'Cape Town',
        province: 'Western Cape',
        description: 'Cape Town, the Mother City, combines stunning natural beauty with vibrant student life and economic opportunity. Home to top universities and a growing tech sector, Cape Town offers diverse room rental options from affordable student digs to upmarket suburbs.',
        metaDescription: 'Search for rooms for rent in Cape Town. Find student accommodation near UCT, CPUT, and Stellenbosch. Affordable rooms from R2,000/month with verified listings.',
        landmarks: ['Table Mountain', 'V&A Waterfront', 'Robben Island', 'Kirstenbosch Gardens', 'Cape Point'],
        universities: ['University of Cape Town (UCT)', 'Cape Peninsula University of Technology (CPUT)', 'Stellenbosch University'],
        transport: ['MyCiTi Bus Service', 'Metrorail Cape Town', 'Golden Arrow Buses'],
        businessHubs: ['Cape Town CBD', 'Century City', 'Claremont', 'Bellville'],
        nearbySuburbs: ['Observatory', 'Rondebosch', 'Mowbray', 'Claremont', 'Bellville', 'Salt River'],
        latitude: -33.9249,
        longitude: 18.4241,
        faqs: [
            {
                question: 'What is the average cost of renting a room in Cape Town?',
                answer: 'Room rentals in Cape Town typically range from R2,000 to R6,000 per month. Student areas like Observatory and Mowbray are more affordable student-friendly, while suburbs like Sea Point and Camps Bay are pricier.'
            },
            {
                question: 'Which areas are best for students in Cape Town?',
                answer: 'Popular student areas include Observatory (close to UCT), Rondebosch, Mowbray, Claremont, and Bellville (near CPUT). These suburbs offer affordability, safety, and proximity to universities.'
            },
            {
                question: 'How good is public transport in Cape Town?',
                answer: 'Cape Town has the MyCiTi BRT system, Golden Arrow buses, and Metrorail services. However, many students prefer cycling or ride-sharing due to limited coverage in some areas.'
            }
        ]
    },

    'durban': {
        name: 'Durban',
        province: 'KwaZulu-Natal',
        description: 'Durban, South Africa\'s coastal gem and busiest port city, offers affordable room rentals for students and young professionals. With warm weather year-round, beachside living, and growing universities, Durban is an attractive option for accommodation seekers.',
        metaDescription: 'Find rooms for rent in Durban, KZN. Browse affordable student accommodation near UKZN, DUT, and the beach. Verified rooms from R1,500/month.',
        landmarks: ['uShaka Marine World', 'Golden Mile Beachfront', 'Moses Mabhida Stadium', 'Durban Botanic Gardens'],
        universities: ['University of KwaZulu-Natal (UKZN)', 'Durban University of Technology (DUT)', 'Mangosuthu University of Technology'],
        transport: ['Durban People Mover', 'Metrorail Durban', 'Municipal Bus Services'],
        businessHubs: ['Durban CBD', 'Umhlanga', 'Gateway', 'Pinetown'],
        nearbySuburbs: ['Glenwood', 'Morningside', 'Musgrave', 'Berea', 'Westville'],
        latitude: -29.8587,
        longitude: 31.0218,
        faqs: [
            {
                question: 'How much does it cost to rent a room in Durban?',
                answer: 'Rooms in Durban range from R1,500 to R4,500 per month. Areas near UKZN and DUT like Glenwood and Berea offer affordable student accommodation, while Umhlanga is more upmarket.'
            },
            {
                question: 'What are the best student areas in Durban?',
                answer: 'Popular student suburbs include Glenwood (near UKZN), Berea, Morningside, Westville (near DUT), and Musgrave. These areas are affordable, safe, and close to universities.'
            },
            {
                question: 'Is Durban a good city for students?',
                answer: 'Yes, Durban offers affordable living, warm weather, beach access, and quality universities. It\'s a great option for students seeking a balanced lifestyle with lower costs than Johannesburg or Cape Town.'
            }
        ]
    }
};

// Helper to get location content with fallback
export const getLocationContent = (citySlug: string): LocationContent | null => {
    return locationContent[citySlug.toLowerCase()] || null;
};

// Get all available cities
export const getAllCities = (): string[] => {
    return Object.keys(locationContent);
};
